import { Component } from "react"

/* Components */
import { Layout } from "../../components"

/* Helpers */
import { utils, withRouter } from "../../helpers"

/* REST API */
import { table as getTable } from "../../api/Electron"

/* SOCKET.IO */
import { io } from "socket.io-client"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"
import { ChartIcon, CheckIcon, CloseIcon } from "../../components/Icons"

/* Redux */
import { connect } from "react-redux"

/* Widgets */
import { ElectronGames, ElectronStatistics } from "../../widgets/ElectronGame"
import { RoundModal } from "../../widgets/Round"
import { Panel as TablePanel, Menu as TableMenu } from "../../widgets/Table"

/* Translate */
import { t } from "../../translates/translate"




/* Page ElectronGame — serves every electron game, the game comes from constants/electron */
class ElectronGame extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            connection: "loading",
            data: null,
        }

        this.socket = null
        this.timing = null
    }


    componentDidMount = () => {

        /* Start Load Data About Table */
        this.load()

        /* Start Listen Socket Events */
        this.socketEvents()

    }


    componentWillUnmount = () => {

        /* Remove Timing */
        if (this.timing) {
            clearTimeout(this.timing)
            this.timing = null
        }

        /* Remove Socket */
        if (this.socket) {
            this.socket.off("connect")
            this.socket.off("disconnect")
            this.socket.disconnect()
            this.socket.close()
            this.socket = null
        }

    }


    /* Load Table Data — the table comes from the game server itself, inside the scope of the token */
    load = (reload = false) => {

        /* Fields */
        const { uid } = this.props.params
        const { game } = this.props

        /* Check Parameters */
        if (!uid || !game) {
            this.setState({ status: "error", data: null })
            return
        }

        /* Refresh the page if necessary */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* START REST API */
        getTable(game.key, { uid }).then((response) => {
            if (response.status === 200) {
                const data = response.data.table
                this.setState({ status: "ok", data })
                this.connect(data)
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: null })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: null })
            }
            else {
                this.setState({ status: "error", data: null })
            }
        }).catch(() => {
            this.setState({ status: "network", data: null })
        })

    }

    /* Connect Socket */
    connect = (data) => {
        try {

            /* Fields */
            const { game } = this.props
            const token = localStorage.getItem("token")

            /* Some game servers expect the admin flag under a different key */
            const auth = game.socket === "type"
                ? { token: `${data.adminToken}`, type: "admin" }
                : { token: `${data.adminToken}`, admin: token }

            this.socket = io(data.backURL, { auth })
        }
        catch {
            this.setState({ connection: "disconnected" })
        }
    }

    /* Socket Events */
    socketEvents = () => {

        if (this.socket) {

            if (this.timing) {
                clearTimeout(this.timing)
                this.timing = null
            }

            if (this.socket.connected) {
                this.setState({ connection: "connected" })
            }

            this.socket.on("connect", () => {
                this.setState({ connection: "connected" })
            })

            this.socket.on("connect_error", () => {
                this.setState({ connection: "disconnected" })
            })

            this.socket.on("disconnect", () => {
                this.setState({ connection: "disconnected" })
            })

        }
        else {
            this.timing = setTimeout(() => {
                clearTimeout(this.timing)
                this.timing = null
                this.socketEvents()
            }, 2000)
        }

    }


    /*
        Panels of the page itself — an electron game runs without a studio and without a dealer,
        so out of the shared panels it only keeps the ones a superadmin owns.
    */
    getPanels = () => {

        /* Fields */
        const { game } = this.props
        const { data } = this.state

        return [{
            key: "statistics",
            text: () => t("game.statistics"),
            hint: () => t("panel.statisticsHint"),
            icon: (size) => <ChartIcon size={size} />,
            tone: "blue",
            wide: true,
            content: () => <ElectronStatistics game={game} uid={data.uid} />,
        }]
    }


    /* Draw Connection State */
    _connection = () => {

        /* Fields */
        const { connection } = this.state

        if (connection === "connected") {
            return (
                <div className="electron-game-header-connection-status connected">
                    <CheckIcon size={12} />
                    {t("game.connectionOk")}
                </div>
            )
        }

        if (connection === "disconnected") {
            return (
                <div className="electron-game-header-connection-status disconnected">
                    <CloseIcon size={12} />
                    {t("game.connectionFail")}
                </div>
            )
        }

        return (
            <div className="electron-game-header-connection-status">
                <LoadingOutlined />
                {t("game.connecting")}
            </div>
        )
    }


    /* Open Panel Action — every modal of the page opens through its own ref */
    open = (key) => {
        if (this._panel) {
            this._panel.open(key)
        }
    }

    /* Open Round Action — the same card every other game of the panel opens */
    openGame = (id) => {

        /* Fields */
        const { data } = this.state

        /* Check Modal */
        if (!this._round || !data) {
            return
        }

        this._round.open({ round: id, table: data.uid })
    }

    /* Reload The List After The Round Card Changed Something */
    reload = () => {
        if (this._games && this._games.reloadGames) {
            this._games.reloadGames()
        }
    }


    render = () => {

        /* Fields */
        const { status, data } = this.state
        const { game, admin } = this.props

        /* Access — an electron game runs without a studio, so only a manager and a superadmin reach it */
        if (!utils.can(admin, "manage")) {
            return <Layout status="permission" />
        }

        /* Preloaders */
        if (status !== "ok") {
            return <Layout status={status} reload={() => this.load(true)} />
        }

        return (
            <div className="page">

                <div className="electron--game-header">
                    <h1>{data.name}</h1>
                    <div className="electron-game-header-connection">
                        <p>{data.slug}</p>
                        {this._connection()}
                    </div>
                </div>

                {/* Panels — an electron game has no studio, so no dealer, no message and no archive */}
                <TableMenu keys={[]} extra={this.getPanels()} open={(key) => this.open(key)} />

                <ElectronGames ref={(ref) => this._games = ref} game={game} uid={data.uid} openGame={(id, number) => this.openGame(id, number)} />

                {/* Round Card */}
                <RoundModal ref={(ref) => this._round = ref} socket={this.socket} close={() => this.reload()} />

                {/* Statistics, Currencies And Settings */}
                <TablePanel ref={(ref) => this._panel = ref} uid={data.uid} blackjack={game.currency === "blackjack"} extra={this.getPanels()} />

            </div>
        )
    }

}


const mapStateToProps = (state) => {
    return {
        operators: state.operators,
        language: state.language,
        admin: state.admin,
    }
}

export default connect(mapStateToProps)(withRouter(ElectronGame))
